/**
 * Pack Size Selector
 *
 * Manages the custom UI-only Pack Size picker rendered by
 * snippets/pack-size-selector.liquid. Emits a `packSizeChanged` DOM event so
 * other modules (custom.js for price/savings display, product-form.js for
 * cart submission) can react without knowing about variant IDs.
 *
 * Shared globals set on window:
 * - window._currentPackCount    : currently selected pack count (1, 2, or 4)
 * - window._onePackBasePrice    : 1-Pack price in cents, from data attribute
 */
(function () {
  'use strict';

  function readOnePackPrice(container) {
    const raw = container.dataset.onePackPrice;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /**
   * Returns true if the given radio input is marked as out-of-stock
   * (has the .disabled class set by pack-size-selector.liquid).
   */
  function isPackUnavailable(input) {
    return input.classList.contains('disabled');
  }

  /**
   * Update the main Add to Cart button and variant hidden input to reflect
   * whether the currently selected pack is available or sold out.
   */
  function updateSubmitButtonState(available) {
    const submitBtn = document.querySelector('button[id^="ProductSubmitButton-"]:not([id^="StickySubmitButton-"])');
    if (!submitBtn) return;

    const btnLabel = submitBtn.querySelector('span:not(.loading__spinner):not(.Add-tocart-text)');
    const variantInput = document.querySelector('input.product-variant-id[name="id"]');
    const notifyBtn = document.getElementById('notify-me-btn');

    if (available) {
      submitBtn.removeAttribute('disabled');
      submitBtn.removeAttribute('aria-disabled');
      submitBtn.classList.remove('product_sold-out');
      if (btnLabel) btnLabel.textContent = 'Add to Cart';
      if (variantInput) variantInput.removeAttribute('disabled');
      if (notifyBtn) notifyBtn.classList.add('hidden');
    } else {
      submitBtn.setAttribute('disabled', 'disabled');
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('product_sold-out');
      if (btnLabel) btnLabel.textContent = 'Sold Out';
      if (variantInput) variantInput.setAttribute('disabled', 'disabled');
      if (notifyBtn) notifyBtn.classList.remove('hidden');
    }

    // Let syncStickyButtonWithMain (custom.js) mirror the new state to the sticky bar
    if (typeof window.updateStickyAddToCartBar === 'function') {
      window.updateStickyAddToCartBar({ available });
    }
  }

  function initSelector(container) {
    if (!container || container.dataset.packSizeInitialized === 'true') return;
    container.dataset.packSizeInitialized = 'true';

    window._onePackBasePrice = readOnePackPrice(container);

    const checked = container.querySelector('input[name="pack_size"]:checked');
    const initialPackCount = checked ? parseInt(checked.value, 10) : 1;
    window._currentPackCount = initialPackCount;

    // Set initial button state based on whether the default pack is available
    if (checked) updateSubmitButtonState(!isPackUnavailable(checked));

    container.addEventListener('change', (event) => {
      if (!event.target || event.target.name !== 'pack_size') return;
      const packCount = parseInt(event.target.value, 10) || 1;
      window._currentPackCount = packCount;

      updateSubmitButtonState(!isPackUnavailable(event.target));

      document.dispatchEvent(
        new CustomEvent('packSizeChanged', {
          detail: { packCount, onePackPrice: window._onePackBasePrice },
        })
      );
    });
  }

  function init() {
    document.querySelectorAll('pack-size-selector').forEach(initSelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-sync window._currentPackCount with the actual :checked radio whenever the
  // page is shown. Fires on both bfcache restores (event.persisted === true) and
  // fresh loads. This guards against a race where deferred scripts run before
  // the browser restores form state after back-navigation from /checkout, which
  // would otherwise leave the global stuck at the server-rendered 1-Pack default
  // even though the UI visibly shows the user's prior 2-/4-Pack selection.
  window.addEventListener('pageshow', () => {
    const checked = document.querySelector('input[name="pack_size"]:checked');
    if (!checked) return;
    const restored = parseInt(checked.value, 10) || 1;
    if (window._currentPackCount !== restored) {
      window._currentPackCount = restored;
      document.dispatchEvent(
        new CustomEvent('packSizeChanged', {
          detail: { packCount: restored, onePackPrice: window._onePackBasePrice },
        })
      );
    }
  });
})();
